import { GAME_BOARD_SIZE, SIZES } from "./constants_v2.js";
import { zoom } from "./zoom_v2.js";
import { identityService } from "./profile/identityService_v2.js";
import { newMatch } from "./matches/newMatch_v2.js";
import { join } from "./matches/join_v2.js";
import { profile } from "./profile/profile_v2.js";
import { help } from "./help_v2.js";
import { currentMatch } from "./matches/currentMatch_v2.js";
import { leaderboard } from "./leaderboard_v2.js";
import { recentMatches } from "./matches/recentMatches_v2.js";
import { hamburger } from "./hamburger_v2.js";
import { escapeHtml } from "./escapeHtml_v2.js";
// These were missing, so renderSessionTimeOutPop's `dasModal` was the
// #dasModal <div> (browsers make element ids globals) and it threw.
import { dasModal } from "./modal_v2.js";
import { colors } from "./colors_v2.js";
import { eventHub } from "./eventHub_v2.js";

/**
 * Returns my best guess if this is on a desktop (vs mobile)
 * @returns {boolean} true means running on a desktop
 */
const runningOnDesktop = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  var isDesktop = false;
  if (userAgent.includes("iphone")) {
    return false;
  }
  if (userAgent.includes("ipad")) {
    return false;
  }
  if (userAgent.includes("android")) {
    return false;
  }
  if (userAgent.includes("windows")) {
    isDesktop = true;
  }
  if (userAgent.includes("mac")) {
    isDesktop = true;
  }
  if (navigator.platform.indexOf("Mac") > -1) {
    isDesktop = true;
  }
  if (navigator.platform.indexOf("Win") > -1) {
    isDesktop = true;
  }

  return isDesktop;
};

/**
 * True when the nav buttons (New, Join, ...) are tucked under the hamburger
 * icon instead of shown on the left. Uses the same rule as the stylesheet
 * (@media (max-width: 500px) hides .sectionNarrow), so it's about the
 * window's width, not the device: a narrow desktop window counts, and an
 * iPad wide enough to show the buttons doesn't. Check it when you need it
 * (not once at startup), since rotating or resizing changes the answer.
 * runningOnDesktop() is for a different question (mouse vs touch features).
 * @returns {boolean}
 */
const navIsUnderHamburger = () =>
  typeof window.matchMedia === "function"
    ? window.matchMedia("(max-width: 500px)").matches
    : Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) <= 500;

const isDOMElement = (obj) => {
  // Check if the object exists and is an instance of the Element interface.
  // HTMLElement is a more specific interface for HTML elements.
  return obj && (obj instanceof Element || obj instanceof HTMLElement);
};

const disableButton = (button) => {
  if (!isDOMElement(button)) {
    console.log(
      "Error: Parameter passed is is not an element in the DOM: " +
        JSON.stringify(button),
    );
    return;
  }
  button.disabled = true;
  button.classList.add("buttonDisabled");
};

const enableButton = (button) => {
  if (!isDOMElement(button)) {
    console.log(
      "Error: Parameter passed is is not an element in the DOM: " +
        JSON.stringify(button),
    );
    return;
  }
  button.disabled = false;
  button.classList.remove("buttonDisabled");
};

const textGotForbiddenChars = (text) => {
  const allowed = /^[a-zA-Z0-9\s'\-_]+$/;
  if (allowed.test(text)) {
    // my regex sucks, therefore:
    if (text.length - text.replaceAll("'", "").length > 1) {
      // one apostophe is okay, as in my name is O'Malley
      // but more than one might be quotes
      return true;
    } else {
      // only contains allowed chars
      // but only one apostophe
      return false;
    }
  }
  return true;
};

/**
 * Called by keydown event handlers for text inputs.
 * Validates that text only contains numbers, letters, spaces, and only one apostrophe.
 * Then it updates the UI as appropriate
 * @param {params} paramsObject e,g, {elements: {message, submitButton}, length: {min, max}, textEntered}
 */
const genericTextInputProcessor = (paramsObject) => {
  const messageElement = paramsObject.elements.message;
  const submitButton = paramsObject.elements.submitButton;
  const min = paramsObject.length.min;
  const max = paramsObject.length.max;
  const textEntered = paramsObject.textEntered;
  var errorCondition = false;
  switch (true) {
    case textEntered.length > max:
      messageElement.textContent = "Must be less than " + max + " characters";
      disableButton(submitButton);
      errorCondition = true;
      break;
    case textEntered.length < min:
      messageElement.textContent = "Must be at least " + min + " character(s)";
      disableButton(submitButton);
      errorCondition = true;
      break;
    case textGotForbiddenChars(textEntered):
      messageElement.textContent = "Can't have forbidden characters";
      disableButton(submitButton);
      errorCondition = true;
      break;
  }
  if (errorCondition === false) {
    messageElement.textContent = "";
    enableButton(submitButton);
  }
};

const generateID = () => {
  let capitalLetters = Array.from(Array(26)).map((_, i) =>
    String.fromCharCode(65 + i),
  ); // Generates 'A' through 'Z'
  let numbers = Array.from(Array(9)).map((_, i) => String(i + 1)); // Generates '1' through '9', because 0 and O are confusing
  capitalLetters.splice(capitalLetters.indexOf("O"), 1); //because 0 and O are confusing

  let ID = ""; //string
  const numPos = Math.floor(Math.random() * 6); // 0-5
  for (let i = 0; i < 7; i++) {
    if (i === numPos) {
      const randoNum = numbers[Math.floor(Math.random() * 9)];
      ID = ID + randoNum;
    } else {
      const randoLetter = capitalLetters[Math.floor(Math.random() * 25)];
      ID = ID + randoLetter;
    }
  }
  return ID;
};

const calculateSectionWidth = () => {
  let viewportWidth = window.innerWidth - zoom.getHexSide() * 1;
  const idealWidth = zoom.getHexSide() * GAME_BOARD_SIZE * 2; //hangs off right hand side a bit
  if (idealWidth > viewportWidth) {
    return viewportWidth + "px";
  } else {
    return idealWidth + "px";
  }
};

const getPlayingAsNameAsHTML = async () => {
  const pAIdentityResponse = await identityService.getPlayingAs();
  if (pAIdentityResponse.status != 200) {
    // session expired
    return;
  }
  const pAIdentity = pAIdentityResponse.identity;
  return `<span class="smcontent"><span class="courier">${escapeHtml(pAIdentity.name)}</span></span>`;
};

const getIdentityNameAsHTML = async (userID) => {
  // Debug logging for utils-app getIdentityNameAsHTML
  console.log("utils-app getIdentityNameAsHTML: userID =", userID, "type =", typeof userID);
  console.log("utils-app getIdentityNameAsHTML: call stack:", new Error().stack);
  
  const identityResponse = await identityService.getIdentity(userID);
  if (identityResponse.status != 200) {
    // session expired
    return;
  }
  const identity = identityResponse.identity;
  return `<span class="smcontent"><span class="courier">${escapeHtml(identity.name)}</span></span>`;
};

const isSizeValid = (size) => {
  if (SIZES.includes(size)) {
    return true;
  } else {
    return false;
  }
};

const removeDiacritics = (char) => {
  if (typeof char !== "string" || char.length !== 1) {
    console.log("Input must be a single character string.");
    return;
  }
  const normalizedChar = char.normalize("NFD");
  const baseChar = normalizedChar.replace(/[\u0300-\u036f]/g, "");
  return baseChar;
};

/**
 * renders all nav sections to be small
 */
const makeNavsSmall = () => {
  newMatch.render("small");
  currentMatch.render("small");
  join.render("small");
  profile.render("small");
  help.render("small");
  leaderboard.render("small");
  recentMatches.render("small");
};

const adjust4Mobile = () => {
  const vw = Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0,
  );
  if (vw <= 500) {
    document.getElementById("sectionHeaderArea").style.display = "none";
    hamburger.close();
  } else {
    document.getElementById("sectionHeaderArea").style.display = "block";
  }
};

/**
 * Based 64 URL encodes a javascript object.
 * (useful for sending it in http headers where certain chars have to be escaped.)
 * @param {*} obj
 * @returns {string} a base64 url encoded string
 */
const base64UrlEncode = (obj) => {
  const json = JSON.stringify(obj);

  // Encode JSON to UTF-8 bytes
  const utf8Bytes = new TextEncoder().encode(json);

  // Convert bytes → regular Base64
  let base64 = btoa(
    Array.from(utf8Bytes, (byte) => String.fromCharCode(byte)).join(""),
  );

  // Convert Base64 → Base64Url
  return base64
    .replace(/=/g, "") // strip '=' padding
    .replace(/\+/g, "-") // convert '+' to '-'
    .replace(/\//g, "_"); // convert '/' to '_'
};

/**
 * The server ended this session (sessionGuard_v2.js saw the authorizer
 * refuse it). There's no way to continue without reloading, so there's no X,
 * just a button that reloads the page.
 */
const renderSessionEndedPop = async () => {
  dasModal.close();
  await new Promise(requestAnimationFrame);
  dasModal.renderNoX(`
    <div class="max flexColumn">
      <p>Your session has ended</p>
      <p>This happens when the password was changed or reset, or the account was deleted, on this or another device.</p>
      <p>Please refresh your browser. If you have an account, log in again.</p>
      <button id="sessionEndedRefresh" class="button workADayButtons">Refresh</button>
    </div>`);
  colors.setButtonColors();
};
eventHub.registerEvent("click", "sessionEndedRefresh", () => {
  window.location.reload();
});

const renderSessionTimeOutPop = async () => {
  dasModal.close();
  await new Promise(requestAnimationFrame);
  dasModal.render(
    `<div class=max>Your session has timed out.<br><br>Please refresh your brower</div>`,
  );
};

/**
 * Sanitize text by escaping HTML special characters to prevent XSS.
 * Escapes quotes too, so the result is safe inside attribute values.
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text with HTML characters escaped
 */
const sanitizeMessage = (text) => escapeHtml(text);

export const utilsApp = {
  adjust4Mobile,
  base64UrlEncode,
  getIdentityNameAsHTML,
  getPlayingAsNameAsHTML,
  calculateSectionWidth,
  generateID,
  genericTextInputProcessor,
  textGotForbiddenChars,
  runningOnDesktop,
  navIsUnderHamburger,
  renderSessionTimeOutPop,
  renderSessionEndedPop,
  isSizeValid,
  removeDiacritics,
  makeNavsSmall,
  sanitizeMessage,
};
